import { useTranslation } from 'react-i18next';

import type { GroupConflict } from '@/api/curatorGroups';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Окно «забрать ученика у другого куратора» (D40, договор §4).
 *
 * ⚠️ ЗДЕСЬ НЕ ГОДИТСЯ ОБЩЕЕ `useConfirm`: оно принимает одну строку, а решение
 * куратор принимает по СПИСКУ — у кого именно и из какой группы он забирает.
 * «Забрать 3 учеников?» без имён — это подпись вслепую.
 *
 * ⚠️ ОКНО НЕ ЗАКРЫВАЕТСЯ ПО ВТОРОМУ ОТКАЗУ. Если другой куратор забрал ученика
 * секундой раньше, сервер снова отвечает 409 — со свежим списком. Закрыть окно
 * значит показать, будто перевод прошёл (договор §4: проверка идёт под замком
 * записи).
 */
export function TakeoverDialog({
  open,
  conflicts,
  again,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  conflicts: GroupConflict[];
  /** Вторая попытка: кого-то уже забрали раньше. */
  again: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const buttons = (
    <div className="flex gap-3">
      <Button variant="secondary" fullWidth onClick={onCancel}>
        {t('curatorGroups.conflictCancel')}
      </Button>
      <Button fullWidth loading={pending} onClick={onConfirm}>
        {t('curatorGroups.conflictTake')}
      </Button>
    </div>
  );

  return (
    // Телефон (§4.9): окно во весь экран, «Отмена / Забрать» прибиты к низу — список бывает длинным.
    <Modal
      open={open}
      onClose={onCancel}
      title={t('curatorGroups.conflictTitle')}
      size="md"
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div data-takeover>
        <p className="text-sm text-on-surface-variant">{t('curatorGroups.conflictHint')}</p>
        {again && <p className="mt-2 text-sm text-error">{t('curatorGroups.conflictAgain')}</p>}
        <ul className="mt-3 space-y-2">
          {conflicts.map((c) => (
            <li key={c.enrollment_id} className="rounded-card bg-surface-container-lowest px-3 py-2 text-sm">
              <p className="font-semibold text-on-surface">{c.student_name}</p>
              <p className="text-on-surface-variant">
                {c.group_name ?? t('curatorGroups.conflictNoGroup')}
                {' · '}
                {c.curators.length
                  ? c.curators.map((p) => p.name).filter(Boolean).join(', ')
                  : t('curatorGroups.conflictNoCurator')}
              </p>
            </li>
          ))}
        </ul>
        {!isMobile && <div className="mt-5">{buttons}</div>}
      </div>
    </Modal>
  );
}
